import { Toaster } from '@ixigo/iui/Toast';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { Fragment, useEffect } from 'react';
import {
	type ApiErrorResponse,
	type BookingSuccessResponse,
	getBookingDetail,
	type TripErrorType,
} from '@/api';
import { ErrorView } from '../-components/error-view';
import { BookingConfirmationContent, TripDetailContent } from './-components';
import TripPageLoading from './-components/loading';
import { useTripBookingActions } from './-store';
import { tripTracker } from './-utils/tracker';

interface TripSearchParams {
	confirmation?: boolean;
	action?: 'reschedule';
}

class TripError extends Error {
	public readonly errorType: TripErrorType;
	public readonly errors: ApiErrorResponse<TripErrorType>['errors'];

	constructor(
		errorType: TripErrorType,
		errors: ApiErrorResponse<TripErrorType>['errors'],
	) {
		super();
		this.errorType = errorType;
		this.errors = errors;

		// Maintains proper stack trace for where our error was thrown (only available on V8)
		if (Error.captureStackTrace) {
			Error.captureStackTrace(this, TripError);
		}
	}
}

function isTripError(error: unknown): error is TripError {
	return (
		typeof error === 'object' &&
		error !== null &&
		'errorType' in error &&
		'errors' in error
	);
}

async function tripLoader(context: {
	params: { bookingId: string };
}): Promise<BookingSuccessResponse> {
	const { bookingId } = context.params;

	// Always fetch full booking data - component will decide which view to show
	const response = await getBookingDetail(bookingId);
	if ('errors' in response) {
		const errorType = response.errors.type || 'API_ERROR';
		throw new TripError(errorType, response.errors);
	}

	return response;
}

export const Route = createFileRoute('/_auth/trips/$bookingId/')({
	validateSearch: (search: Record<string, unknown>): TripSearchParams => {
		return {
			confirmation: search.confirmation === true,
			action: search.action === 'reschedule' ? 'reschedule' : undefined,
		};
	},
	ssr: false,
	loader: tripLoader,
	component: TripPage,
	pendingComponent: () => {
		const { confirmation } = Route.useSearch();
		const { bookingId } = Route.useParams();
		return (
			<TripPageLoading
				confirmation={confirmation === true}
				bookingId={bookingId}
			/>
		);
	},
	pendingMs: 300,
	pendingMinMs: 600,
	errorComponent: ({ error }) => {
		if (isTripError(error)) {
			return (
				<ErrorView
					title={error.errors.title}
					description={error.errors.message}
					errorType={error.errorType}
					fullScreen
				/>
			);
		}
		return (
			<ErrorView
				title=""
				description={error.message}
				errorType="GENERIC_ERROR"
				fullScreen
			/>
		);
	},
});

function TripPage() {
	const bookingData = Route.useLoaderData();
	const { confirmation } = Route.useSearch();
	const { setBookingData } = useTripBookingActions();

	useEffect(() => {
		setBookingData(bookingData.data);
	}, [setBookingData, bookingData]);

	const isConfirmationView = confirmation === true;

	useEffect(() => {
		if (isConfirmationView) {
			tripTracker.tracker.book();
		}
	}, [isConfirmationView]);

	return (
		<Fragment>
			<Toaster />
			{isConfirmationView ? (
				<BookingConfirmationContent {...bookingData.data} />
			) : (
				<TripDetailContent {...bookingData.data} />
			)}
			<Outlet />
		</Fragment>
	);
}
